/**
 * Melodic Dictation Drill — free-practice mode.
 *
 * Hear a melody in key context; reproduce it via solfège or piano input.
 */

import {
  useReducer,
  useRef,
  useEffect,
  useCallback,
  useState,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CoachSessionDoneCard } from "../components/CoachSessionDone.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import { ModeSwitcherBar, MODE_COLOR as PICKER_MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { ExerciseLessonLink } from "../components/ExerciseLessonLink.js";

import { useListenSession } from "../lib/use-listen-session.js";
import { useExerciseSession } from "../lib/use-exercise-session.js";
import { nextRoundSeed } from "../lib/round-seed.js";
import { ListenAutoAdvanceControl } from "../components/ListenAutoAdvanceControl.js";
import {
  buildMelodicDictationExerciseRound,
  CADENCE_TEMPO_OPTS,
  DEFAULT_MELODIC_DICTATION_EXERCISE_CONFIG,
  dictationDrillStatsKey,
  dictationExerciseItemId,
  gradeDictation,
  type MelodicDictationExerciseConfig,
  type MelodicDictationExerciseRound,
  type DictationGrading,
  type DictationMelodyLength,
  type DictationRange,
  type ScaleDegreeSequence,
  type ScaleDegree,
  type Exercise,
} from "@music-app/exercise-generator";
import { createAudioPlayer, type AudioPlayer } from "@music-app/audio-engine-web";
import {
  useCardsStore,
  usePrefsStore,
  getDrillStats,
  upsertDictationDrillStats,
  getChallengeData,
  recordChallengeAttempt,
  recordReview as dbRecordReview,
  type DrillStatsRecord,
} from "@music-app/persistence";
import { exerciseReviewSource, type Rating } from "@music-app/learning-system";
import { ChallengeTimer } from "../components/ChallengeTimer.js";
import { ChallengeScoreCard } from "../components/ChallengeScoreCard.js";
import { useChallengeTimer } from "../lib/use-challenge-timer.js";
import { DictationInput } from "@music-app/ui-components";
import { createUISoundPlayer, type UISoundPlayer } from "../lib/ui-sounds.js";

// ─── Constants ────────────────────────────────────────────────────────────────

const SESSION_LENGTH_PRESETS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;

const TONIC_OPTIONS: { label: string; midi: number }[] = [
  { label: "C",  midi: 60 }, { label: "G",  midi: 67 },
  { label: "D",  midi: 62 }, { label: "A",  midi: 69 },
  { label: "E",  midi: 64 }, { label: "F",  midi: 65 },
  { label: "B♭", midi: 70 }, { label: "E♭", midi: 63 },
  { label: "A♭", midi: 68 }, { label: "D♭", midi: 61 },
];

const LENGTH_OPTIONS: { value: DictationMelodyLength; label: string }[] = [
  { value: 3, label: "3 notes" },
  { value: 4, label: "4 notes" },
  { value: 5, label: "5 notes" },
  { value: 6, label: "6 notes" },
  { value: 7, label: "7 notes" },
];

const MAJOR_SOLFEGE = ["do", "re", "mi", "fa", "sol", "la", "ti"] as const;
const CHROMATIC_LABELS: Readonly<Record<string, string>> = {
  "1♯": "di", "2♯": "ri", "4♯": "fi", "6♭": "le", "7♭": "te",
};

function sdToSolfege(sd: ScaleDegree): string {
  const base = MAJOR_SOLFEGE[((sd.degree - 1) % 7 + 7) % 7]!;
  if (!sd.alteration) return base;
  return CHROMATIC_LABELS[`${sd.degree}${sd.alteration}`] ?? `${base}${sd.alteration}`;
}

// ─── State machine ────────────────────────────────────────────────────────────

type DictationDrillPhase = "config" | "playing" | "awaiting" | "graded" | "reveal" | "done";

interface DictationDrillState {
  phase: DictationDrillPhase;
  config: MelodicDictationExerciseConfig;
  sessionLength: number;
  round: MelodicDictationExerciseRound | null;
  roundNumber: number;
  sequence: ScaleDegreeSequence;
  grading: DictationGrading | null;
  wasTimeout: boolean;
  accuracySum: number;
  roundsGraded: number;
}

type DictationDrillAction =
  | { type: "START"; config: MelodicDictationExerciseConfig; sessionLength: number; round: MelodicDictationExerciseRound }
  | { type: "PLAYBACK_DONE" }
  | { type: "CHANGE_SEQUENCE"; sequence: ScaleDegreeSequence }
  | { type: "SUBMIT"; grading: DictationGrading }
  | { type: "TIMEOUT"; grading: DictationGrading }
  | { type: "NEXT_ROUND"; round: MelodicDictationExerciseRound }
  | { type: "SESSION_DONE" }
  | { type: "STOP" };

const INITIAL_STATE: DictationDrillState = {
  phase: "config",
  config: DEFAULT_MELODIC_DICTATION_EXERCISE_CONFIG,
  sessionLength: DEFAULT_SESSION_LENGTH,
  round: null,
  roundNumber: 0,
  sequence: [],
  grading: null,
  wasTimeout: false,
  accuracySum: 0,
  roundsGraded: 0,
};

function dictationDrillReducer(
  state: DictationDrillState,
  action: DictationDrillAction,
): DictationDrillState {
  switch (action.type) {
    case "START":
      return {
        ...INITIAL_STATE,
        phase: "playing",
        config: action.config,
        sessionLength: action.sessionLength,
        round: action.round,
        roundNumber: 1,
      };

    case "PLAYBACK_DONE": {
      if (state.phase !== "playing") return state;
      return {
        ...state,
        phase: state.config.drillMode === "listening" ? "reveal" : "awaiting",
      };
    }

    case "CHANGE_SEQUENCE": {
      if (state.phase !== "awaiting") return state;
      return { ...state, sequence: action.sequence };
    }

    case "SUBMIT": {
      if (state.phase !== "awaiting") return state;
      return {
        ...state,
        phase: "graded",
        grading: action.grading,
        wasTimeout: false,
        accuracySum: state.accuracySum + action.grading.accuracy * 100,
        roundsGraded: state.roundsGraded + 1,
      };
    }

    case "TIMEOUT": {
      if (state.phase !== "awaiting") return state;
      return {
        ...state,
        phase: "graded",
        grading: action.grading,
        wasTimeout: true,
        accuracySum: state.accuracySum + action.grading.accuracy * 100,
        roundsGraded: state.roundsGraded + 1,
      };
    }

    case "NEXT_ROUND":
      return {
        ...state,
        phase: "playing",
        round: action.round,
        roundNumber: state.roundNumber + 1,
        sequence: [],
        grading: null,
        wasTimeout: false,
      };

    case "SESSION_DONE":
      return { ...state, phase: "done" };

    case "STOP":
      return { ...INITIAL_STATE };
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function MelodicDictationExercisePage(): ReactNode {
  const [state, dispatch] = useReducer(dictationDrillReducer, INITIAL_STATE);
  const playerRef = useRef<AudioPlayer | null>(null);
  const uiSoundRef = useRef<UISoundPlayer | null>(null);

  const [searchParams] = useSearchParams();
  const VALID_MODES: ExerciseMode[] = ["listen", "practice", "review", "challenge"];
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: ExerciseMode = rawMode && VALID_MODES.includes(rawMode) ? rawMode : "practice";
  const listenMode = mode === "listen";
  const challengeMode = mode === "challenge";
  const coached = searchParams.get("coached") === "true";
  const coachItemCount = Math.max(1, parseInt(searchParams.get("itemCount") ?? "10", 10));
  const practiceMode = mode === "practice";
  const nodeId = searchParams.get("nodeId") ?? undefined;
  const from = searchParams.get("from") ?? undefined;

  const challengeStartedAtRef = useRef<string | null>(null);
  const challengeCorrectRef = useRef(0);
  const challengeResponseTimesRef = useRef<number[]>([]);
  const challengeResponseStartRef = useRef<number | null>(null);
  const previousBestRef = useRef<number | null | undefined>(undefined);

  const { recordPlay } = useListenSession("dictation", listenMode, nodeId);
  const { recordItem } = useExerciseSession("dictation", mode === "review" ? "practice" : "drill", !listenMode && !challengeMode);

  const applyReview = useCardsStore((s) => s.applyReview);
  const instrument = usePrefsStore((s) => s.instrument);
  const uiSoundsEnabled = usePrefsStore((s) => s.uiSoundsEnabled);
  const cadenceTempo = usePrefsStore((s) => s.cadenceTempo);

  useEffect(() => {
    const player = createAudioPlayer();
    playerRef.current = player;
    return () => {
      player.stop();
      player.destroy();
      uiSoundRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !instrument) return;
    void (async () => {
      await player.init();
      if (!player.isInstrumentLoaded(instrument)) {
        await player.loadInstrument(instrument);
      }
      player.setInstrument(instrument);
    })();
  }, [instrument]);

  useEffect(() => {
    if (state.phase !== "graded" || !state.round || !state.grading) return;
    void upsertDictationDrillStats(
      dictationDrillStatsKey(state.config),
      Math.round(state.grading.accuracy * 100),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  useEffect(() => {
    if (state.phase !== "reveal" || !state.round) return;
    void upsertDictationDrillStats(dictationDrillStatsKey(state.config), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  useEffect(() => {
    if (!listenMode || state.phase !== "reveal") return;
    // Dictation rounds lack stable item IDs; use nodeId + round slot so each play
    // produces a unique event for the exposure counter.
    recordPlay(`${nodeId ?? "melodic-dictation-narrow"}::${state.roundNumber}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listenMode, state.phase, state.roundNumber]);

  const playRound = useCallback(
    async (round: MelodicDictationExerciseRound, mode: MelodicDictationExerciseConfig["drillMode"]) => {
      const player = playerRef.current;
      if (!player) return;
      player.stop();
      await player.init();
      const pref = usePrefsStore.getState().instrument;
      if (!player.isInstrumentLoaded(pref)) await player.loadInstrument(pref);
      player.setInstrument(pref);
      await player.play(round.audioPlan);
      if (mode === "listening") {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 2000));
        await player.play(round.teachAudio);
      }
      recordItem(`${nodeId ?? "melodic-dictation-narrow"}::${state.roundNumber}`);
      dispatch({ type: "PLAYBACK_DONE" });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recordItem, nodeId, state.roundNumber],
  );

  const replay = useCallback(() => {
    const player = playerRef.current;
    if (!player || !state.round) return;
    void player.play(state.round.audioPlan);
  }, [state.round]);

  const replayTeach = useCallback(() => {
    const player = playerRef.current;
    if (!player || !state.round) return;
    void player.play(state.round.teachAudio);
  }, [state.round]);

  const startDrill = useCallback(
    (config: MelodicDictationExerciseConfig, sessionLength: number) => {
      const round = buildMelodicDictationExerciseRound(config, nextRoundSeed(), CADENCE_TEMPO_OPTS[cadenceTempo]);
      dispatch({ type: "START", config, sessionLength, round });
      void playRound(round, config.drillMode);
    },
    [playRound, cadenceTempo],
  );

  const handleSubmit = useCallback(() => {
    if (!state.round || state.phase !== "awaiting") return;
    const stub: Exercise = {
      itemId: "",
      exerciseType: "melodic-dictation",
      audio: state.round.audioPlan,
      prompt: "",
      options: [],
      correctAnswerId: "",
      meta: {
        seed: 0,
        explanation: "",
        distractorCount: 0,
        expectedSequence: state.round.expectedSequence,
      },
    };
    const grading = gradeDictation(stub, state.sequence);
    dispatch({ type: "SUBMIT", grading });

    if (uiSoundsEnabled) {
      if (!uiSoundRef.current) uiSoundRef.current = createUISoundPlayer();
      uiSoundRef.current.play(grading.correct ? "correct" : "wrong");
    }
    if (!grading.correct) {
      const player = playerRef.current;
      const teachPlan = state.round.teachAudio;
      if (player) window.setTimeout(() => { void player.play(teachPlan); }, 600);
    }
  }, [state.round, state.phase, state.sequence, uiSoundsEnabled]);

  const nextRound = useCallback(() => {
    if (!state.round) return;
    if (state.roundNumber >= state.sessionLength) {
      dispatch({ type: "SESSION_DONE" });
      return;
    }
    const round = buildMelodicDictationExerciseRound(state.config, nextRoundSeed(), CADENCE_TEMPO_OPTS[cadenceTempo]);
    dispatch({ type: "NEXT_ROUND", round });
    void playRound(round, state.config.drillMode);
  }, [state.config, state.round, state.roundNumber, state.sessionLength, playRound, cadenceTempo]);

  const handleStop = useCallback(() => {
    playerRef.current?.stop();
    dispatch({ type: "STOP" });
  }, []);

  const handleTimeout = useCallback(() => {
    if (!state.round || state.phase !== "awaiting") return;
    const stub: Exercise = {
      itemId: "",
      exerciseType: "melodic-dictation",
      audio: state.round.audioPlan,
      prompt: "",
      options: [],
      correctAnswerId: "",
      meta: {
        seed: 0,
        explanation: "",
        distractorCount: 0,
        expectedSequence: state.round.expectedSequence,
      },
    };
    const grading = gradeDictation(stub, state.sequence);
    dispatch({ type: "TIMEOUT", grading });
  }, [state.round, state.phase, state.sequence]);

  // Challenge: mount — load previous best + reset refs
  useEffect(() => {
    if (!challengeMode) return;
    challengeStartedAtRef.current = new Date().toISOString();
    challengeCorrectRef.current = 0;
    challengeResponseTimesRef.current = [];
    challengeResponseStartRef.current = null;
    void getChallengeData("dictation").then((d) => {
      previousBestRef.current = d.best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeMode]);

  // Awaiting — record response start time (used for all graded modes).
  useEffect(() => {
    if (state.phase !== "awaiting") return;
    challengeResponseStartRef.current = Date.now();
  }, [state.phase]);

  // Graded — write review record and update FSRS card state per mode.
  useEffect(() => {
    if (state.phase !== "graded" || !state.grading || !state.round) return;
    if (listenMode) return;
    const correct = state.grading.correct;
    const responseMs = challengeResponseStartRef.current
      ? Date.now() - challengeResponseStartRef.current
      : 8000;
    const itemId = dictationExerciseItemId(state.config, state.round.instanceIndex);
    const source = exerciseReviewSource({ challengeMode, fsrsEnabled: false });
    void dbRecordReview({
      itemId,
      sessionId: "",
      rating: correct ? "good" : "again",
      correct,
      responseMs,
      chosenAnswerId: null,
      source,
    });
    if (challengeMode) {
      challengeResponseTimesRef.current.push(responseMs);
      if (correct) challengeCorrectRef.current += 1;
      const rating: Rating = correct ? (responseMs < 5000 ? "good" : "hard") : "again";
      void applyReview(itemId, rating);
    }
  }, [state.phase, state.grading, state.round, state.config, challengeMode, listenMode, applyReview]);

  // Challenge: done — record attempt
  useEffect(() => {
    if (state.phase !== "done" || !challengeMode) return;
    const correct = challengeCorrectRef.current;
    const times = challengeResponseTimesRef.current;
    const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
    void recordChallengeAttempt({
      challengeKey: "dictation",
      startedAt: challengeStartedAtRef.current ?? new Date().toISOString(),
      items: state.sessionLength,
      correct,
      accuracy: state.sessionLength > 0 ? correct / state.sessionLength : 0,
      averageResponseMs: Math.round(avgMs),
    });
  }, [state.phase, challengeMode, state.sessionLength]);

  // Challenge: timeout — auto-advance after 600ms
  useEffect(() => {
    if (!state.wasTimeout) return;
    const t = window.setTimeout(nextRound, 600);
    return () => window.clearTimeout(t);
  }, [state.wasTimeout, nextRound]);

  const { remainingMs } = useChallengeTimer({
    active: challengeMode && state.phase === "awaiting",
    timeMs: 8000,
    onTimeout: handleTimeout,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key.toLowerCase() === "r" && state.phase !== "config" && state.phase !== "done" && !challengeMode) {
        e.preventDefault();
        replay();
        return;
      }
      if ((state.phase === "graded" || state.phase === "reveal") &&
          !challengeMode &&
          (e.key === " " || e.key === "Enter")) {
        e.preventDefault();
        nextRound();
        return;
      }
      if (state.phase === "awaiting" && !challengeMode && e.key === "Enter" && state.sequence.length > 0) {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.phase, state.sequence.length, nextRound, replay, handleSubmit, challengeMode]);

  const modeColor = PICKER_MODE_COLOR[mode];

  if (state.phase === "config") {
    if (challengeMode) return <MelodicDictationExerciseChallengeStart onStart={startDrill} />;
    if (coached) return <MelodicDictationExerciseCoachStart nodeId={searchParams.get("nodeId") ?? undefined} itemCount={coachItemCount} onStart={startDrill} />;
    return <DictationDrillConfigScreen mode={mode} onStart={startDrill} />;
  }

  if (state.phase === "done") {
    if (coached) return <CoachSessionDoneCard />;
    if (challengeMode) {
      const times = challengeResponseTimesRef.current;
      const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
      return (
        <ChallengeScoreCard
          items={state.sessionLength}
          correct={challengeCorrectRef.current}
          averageResponseMs={avgMs}
          previousBest={previousBestRef.current ?? null}
          onAgain={() => startDrill(
            { ...state.config, drillMode: "graded" },
            state.sessionLength,
          )}
          onStop={handleStop}
        />
      );
    }
    return (
      <DictationDrillSummary
        config={state.config}
        sessionLength={state.sessionLength}
        accuracySum={state.accuracySum}
        roundsGraded={state.roundsGraded}
        onAgain={() => startDrill(state.config, state.sessionLength)}
        onStop={handleStop}
      />
    );
  }

  return (
    <DictationDrillLoop
      state={state}
      onChangeSequence={(seq) => dispatch({ type: "CHANGE_SEQUENCE", sequence: seq })}
      onSubmit={handleSubmit}
      onNext={nextRound}
      onReplay={replay}
      onReplayTeach={replayTeach}
      onStop={handleStop}
      listenMode={listenMode}
      practiceMode={practiceMode}
      challengeMode={challengeMode}
      challengeRemainingMs={remainingMs}
      modeColor={modeColor}
      from={from}
    />
  );
}

// ─── Listen auto-start ────────────────────────────────────────────────────────

// ─── Coached listen auto-start ───────────────────────────────────────────────

function MelodicDictationExerciseCoachStart({
  nodeId,
  itemCount,
  onStart,
}: {
  nodeId: string | undefined;
  itemCount: number;
  onStart: (config: MelodicDictationExerciseConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    const range =
      nodeId === "melodic-dictation-medium" || nodeId === "melodic-dictation-chromatic"
        ? "wide" as const
        : "narrow" as const;
    const chromatic = nodeId === "melodic-dictation-chromatic";
    onStart(
      { ...DEFAULT_MELODIC_DICTATION_EXERCISE_CONFIG, range, chromatic, drillMode: "listening" },
      itemCount,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting listen session…</p>
    </div>
  );
}

// ─── Challenge auto-start ────────────────────────────────────────────────────

function MelodicDictationExerciseChallengeStart({
  onStart,
}: {
  onStart: (config: MelodicDictationExerciseConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    onStart({ ...DEFAULT_MELODIC_DICTATION_EXERCISE_CONFIG, drillMode: "graded" }, 20);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-mono text-xs uppercase tracking-[0.18em] text-inkFaded">Starting challenge…</p>
    </div>
  );
}

// ─── Config persistence ───────────────────────────────────────────────────────

const DICTATION_DRILL_LS_KEY = "melodic-dictation-drill-config-v2";

interface DictationDrillSavedConfig {
  sessionLength: number;
  mode: ExerciseMode;
  length: DictationMelodyLength;
  range: DictationRange;
  chromatic: boolean;
  tonic: MelodicDictationExerciseConfig["tonic"];
  fixedTonicMidi: number;
}

const DICTATION_DRILL_DEFAULTS: DictationDrillSavedConfig = {
  sessionLength: DEFAULT_SESSION_LENGTH,
  mode: "practice",
  length: DEFAULT_MELODIC_DICTATION_EXERCISE_CONFIG.length,
  range: DEFAULT_MELODIC_DICTATION_EXERCISE_CONFIG.range,
  chromatic: DEFAULT_MELODIC_DICTATION_EXERCISE_CONFIG.chromatic,
  tonic: DEFAULT_MELODIC_DICTATION_EXERCISE_CONFIG.tonic,
  fixedTonicMidi: DEFAULT_MELODIC_DICTATION_EXERCISE_CONFIG.fixedTonicMidi,
};

function loadDictationDrillConfig(): DictationDrillSavedConfig {
  try {
    const raw = localStorage.getItem(DICTATION_DRILL_LS_KEY);
    if (raw) {
      return {
        ...DICTATION_DRILL_DEFAULTS,
        ...(JSON.parse(raw) as Partial<DictationDrillSavedConfig>),
      };
    }
  } catch { /* ignore */ }
  return DICTATION_DRILL_DEFAULTS;
}

function saveDictationDrillConfig(cfg: DictationDrillSavedConfig): void {
  try { localStorage.setItem(DICTATION_DRILL_LS_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
}

// ─── Config screen ────────────────────────────────────────────────────────────

// Preselect range + chromaticism for the lesson's node when arriving via
// "Practice this" (`?nodeId=…`). Mirrors `MelodicDictationExerciseCoachStart`.
function dictationNodeConfigPatch(
  nodeId: string | undefined,
): Partial<ReturnType<typeof loadDictationDrillConfig>> {
  if (nodeId === "melodic-dictation-narrow") return { range: "narrow", chromatic: false };
  if (nodeId === "melodic-dictation-medium") return { range: "wide", chromatic: false };
  if (nodeId === "melodic-dictation-chromatic") return { range: "wide", chromatic: true };
  return {};
}

interface DictationDrillConfigScreenProps {
  mode: ExerciseMode;
  onStart: (config: MelodicDictationExerciseConfig, sessionLength: number) => void;
}

function DictationDrillConfigScreen({ mode, onStart }: DictationDrillConfigScreenProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const nodeId = searchParams.get("nodeId") ?? undefined;
  const [initConfig] = useState(() => ({ ...loadDictationDrillConfig(), ...dictationNodeConfigPatch(nodeId) }));

  const [sessionLength, setSessionLength] = useState(initConfig.sessionLength);
  const [showCustomLength, setShowCustomLength] = useState(
    !SESSION_LENGTH_PRESETS.includes(initConfig.sessionLength),
  );
  const modeColor = PICKER_MODE_COLOR[mode];
  const [length, setLength] = useState<DictationMelodyLength>(initConfig.length);
  const [range, setRange] = useState<DictationRange>(initConfig.range);
  const [chromatic, setChromatic] = useState(initConfig.chromatic);
  const [tonic, setTonic] = useState<MelodicDictationExerciseConfig["tonic"]>(initConfig.tonic);
  const [fixedTonicMidi, setFixedTonicMidi] = useState(initConfig.fixedTonicMidi);
  const [historyStats, setHistoryStats] = useState<DrillStatsRecord | null | undefined>(undefined);

  useEffect(() => {
    const cfg: MelodicDictationExerciseConfig = {
      length, range, chromatic, tonic, fixedTonicMidi,
      drillMode: mode === "listen" ? "listening" : "graded",
    };
    void getDrillStats(dictationDrillStatsKey(cfg)).then((s) => setHistoryStats(s ?? null));
  }, [length, range, chromatic, tonic, fixedTonicMidi, mode]);

  useEffect(() => {
    saveDictationDrillConfig({
      sessionLength, mode, length, range, chromatic, tonic, fixedTonicMidi,
    });
  }, [sessionLength, mode, length, range, chromatic, tonic, fixedTonicMidi]);

  const handleStart = () => {
    onStart({ length, range, chromatic, tonic, fixedTonicMidi, drillMode: mode === "listen" ? "listening" : "graded" }, sessionLength);
  };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        title="Melodic Dictation"
        subhead="Hear a melody in key context — reproduce it note by note."
      />
      <ModeSwitcherBar current={mode} exercisePath="/exercise/dictation" />
      <ExerciseLessonLink nodeIds={["melodic-dictation-narrow", "melodic-dictation-medium", "melodic-dictation-chromatic"]} />

      <div className="space-y-5 mb-10">
        <ConfigRow label="Exercises per session">
          <div className="flex items-center gap-3 flex-wrap">
            {SESSION_LENGTH_PRESETS.map((n) => {
              const active = !showCustomLength && sessionLength === n;
              return (
                <button
                  key={n}
                  type="button"
                  aria-pressed={active}
                  onClick={() => { setSessionLength(n); setShowCustomLength(false); }}
                  className={`px-4 py-2 text-sm border transition-colors ${
                    active ? "" : "bg-paper text-ink border-ink/20 hover:border-ink"
                  }`}
                  style={active ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
                >
                  {n}
                </button>
              );
            })}
            <button
              type="button"
              aria-pressed={showCustomLength}
              onClick={() => setShowCustomLength(true)}
              className={`px-4 py-2 text-sm border transition-colors ${
                showCustomLength ? "" : "bg-paper text-ink border-ink/20 hover:border-ink"
              }`}
              style={showCustomLength ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
            >
              Other…
            </button>
            {showCustomLength && (
              <input
                type="number"
                min={1}
                max={50}
                value={sessionLength}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 50) setSessionLength(v);
                }}
                className="w-16 px-3 py-2 text-sm border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
                aria-label="Custom number of exercises"
                autoFocus
              />
            )}
          </div>
        </ConfigRow>

        <ConfigRow label="Melody length">
          <NativeSelect
            value={String(length)}
            onChange={(v) => setLength(Number(v) as DictationMelodyLength)}
            options={LENGTH_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))}
          />
        </ConfigRow>

        <ConfigRow label="Range">
          <NativeSelect
            value={range}
            onChange={(v) => setRange(v as DictationRange)}
            options={[
              { value: "narrow", label: "Narrow  (within a 4th)" },
              { value: "wide",   label: "Wide  (up to an octave)" },
            ]}
          />
        </ConfigRow>

        <ConfigRow label="Chromatic notes">
          <NativeSelect
            value={chromatic ? "on" : "off"}
            onChange={(v) => setChromatic(v === "on")}
            options={[
              { value: "off", label: "Off  (diatonic only)" },
              { value: "on",  label: "On  (♯4 and ♭6 variants)" },
            ]}
          />
        </ConfigRow>

        <ConfigRow label="Tonic">
          <div className="flex gap-3 flex-wrap items-center">
            <NativeSelect
              value={tonic}
              onChange={(v) => setTonic(v as MelodicDictationExerciseConfig["tonic"])}
              options={[
                { value: "random", label: "Random" },
                { value: "fixed",  label: "Fixed" },
              ]}
            />
            {tonic === "fixed" && (
              <NativeSelect
                value={String(fixedTonicMidi)}
                onChange={(v) => setFixedTonicMidi(Number(v))}
                options={TONIC_OPTIONS.map((t) => ({ value: String(t.midi), label: t.label }))}
              />
            )}
          </div>
        </ConfigRow>
      </div>

      {historyStats !== undefined && historyStats !== null && historyStats.played > 0 && (
        <div className="mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">History</p>
          <div className="border border-ink/10">
            <div className="grid grid-cols-[1fr_5rem_5rem] px-4 py-2 border-b border-ink/10">
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Config</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Played</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Avg %</span>
            </div>
            <div className="grid grid-cols-[1fr_5rem_5rem] px-4 py-2.5">
              <span className="font-mono text-xs text-ink">
                {length} notes · {range} · {chromatic ? "chromatic" : "diatonic"}
              </span>
              <span className="font-mono text-xs text-ink text-right tabular-nums">
                {historyStats.played}
              </span>
              <span className="font-mono text-xs text-inkFaded text-right tabular-nums">
                {historyStats.accuracySum !== undefined
                  ? `${Math.round(historyStats.accuracySum / historyStats.played)}%`
                  : "—"}
              </span>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleStart}
        className={`mt-4 ${buttonClasses("primary")}`}
        style={{ backgroundColor: modeColor }}
      >
        Start →
      </button>
    </div>
  );
}

// ─── Drill loop ───────────────────────────────────────────────────────────────

interface DictationDrillLoopProps {
  state: DictationDrillState;
  onChangeSequence: (seq: ScaleDegreeSequence) => void;
  onSubmit: () => void;
  onNext: () => void;
  onReplay: () => void;
  onReplayTeach: () => void;
  onStop: () => void;
  listenMode: boolean;
  practiceMode: boolean;
  challengeMode: boolean;
  challengeRemainingMs: number;
  modeColor: string;
  from?: string;
}

function DictationDrillLoop({
  state,
  onChangeSequence,
  onSubmit,
  onNext,
  onReplay,
  onReplayTeach,
  onStop,
  listenMode,
  practiceMode,
  challengeMode,
  challengeRemainingMs,
  modeColor,
  from,
}: DictationDrillLoopProps): ReactNode {
  const { phase, round, roundNumber, sessionLength, config, sequence, grading } = state;

  const isPlaying  = phase === "playing";
  const isAwaiting = phase === "awaiting";
  const isGraded   = phase === "graded";
  const isReveal   = phase === "reveal";

  const isLastRound = roundNumber >= sessionLength;
  const answeredCount = isGraded || isReveal ? roundNumber : roundNumber - 1;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <div className="flex items-center gap-3 mb-4">
        {from === "today" && (
          <>
            <Link
              to="/"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Today
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {from === "learn" && (
          <>
            <Link
              to="/learn"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Learn
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {!from && (
          <>
            <Link
              to="/exercise"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Ear Training
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        <button
          type="button"
          onClick={onStop}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
          aria-label="Return to drill settings"
        >
          ← Settings
        </button>
      </div>
      <ModeSwitcherBar current={challengeMode ? "challenge" : practiceMode ? "review" : listenMode ? "listen" : "practice"} />

      {challengeMode && isAwaiting && (
        <ChallengeTimer remainingMs={challengeRemainingMs} totalMs={8000} />
      )}

      <div className="flex items-baseline justify-between mb-1">
        <h1 className="font-serif text-4xl font-medium text-ink">Melodic Dictation</h1>
        <p className="font-mono text-xs text-inkFaded tabular-nums" aria-label={`Exercise ${roundNumber} of ${sessionLength}`}>
          {roundNumber} of {sessionLength}
        </p>
      </div>
      <p className="font-mono text-xs text-inkFaded mb-4">
        {config.length} notes · {config.range} · {config.chromatic ? "chromatic" : "diatonic"}
        {" · "}
        {config.tonic === "random" ? "random tonic" : "fixed tonic"}
      </p>

      <div
        className="w-full h-0.5 bg-ink/10 mb-2"
        role="progressbar"
        aria-valuenow={roundNumber}
        aria-valuemin={1}
        aria-valuemax={sessionLength}
      >
        <div
          className="h-full transition-all duration-300"
          style={{ width: `${(roundNumber / sessionLength) * 100}%`, backgroundColor: modeColor }}
        />
      </div>

      <div className="flex justify-between font-mono text-xs text-inkFaded mb-6">
        {config.drillMode !== "listening" ? (
          <span>
            {state.roundsGraded > 0
              ? `avg ${Math.round(state.accuracySum / state.roundsGraded)}% · ${answeredCount} answered`
              : `${answeredCount} answered`}
          </span>
        ) : (
          <span>{answeredCount} of {sessionLength} listened</span>
        )}
        {isPlaying && <span className="animate-pulse">Playing…</span>}
      </div>

      {round && (
        <div className="mb-4 font-mono text-xs text-inkFaded">
          Key: <span className="text-ink">{round.keyName}</span>
        </div>
      )}

      {/* Prompt */}
      <div className="mb-4 min-h-[2.5rem] flex items-center">
        {isPlaying && (
          <p className="font-serif italic text-xl text-inkSoft" aria-live="polite">
            Listen to the melody…
          </p>
        )}
        {isAwaiting && (
          <p className="font-serif italic text-xl text-ink" aria-live="polite">
            Notate the melody you heard.
          </p>
        )}
        {isReveal && round && (
          <p className="font-serif italic text-xl text-ink" aria-live="polite">
            The melody was:
          </p>
        )}
        {isGraded && grading && (
          <div aria-live="polite">
            <p className="font-serif italic text-ink">
              {state.wasTimeout
                ? `Time! ${Math.round(grading.accuracy * 100)}% — ${grading.explanation}`
                : grading.correct
                  ? "Perfect — every note correct."
                  : `${Math.round(grading.accuracy * 100)}% — ${grading.explanation}`}
            </p>
            {!challengeMode && (
              <p className="font-mono text-[10px] mt-1" style={{ color: practiceMode ? modeColor : "#9A9390" }}>
                {practiceMode ? "Counts toward Today." : "No schedule changes."}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Expected sequence (reveal + wrong graded) */}
      {(isReveal || (isGraded && grading && !grading.correct)) && round && (
        <div className="mb-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded mb-1.5">
            {isReveal ? "Melody" : "Expected"}
          </p>
          <div className="flex flex-wrap gap-1" role="list" aria-label="Expected melody">
            {round.expectedSequence.map((sd, i) => (
              <span
                key={i}
                role="listitem"
                className="inline-block px-2 py-1 text-sm font-serif italic min-w-[2.5rem] text-center border"
                style={{ color: modeColor, borderColor: `${modeColor}50`, backgroundColor: `${modeColor}15` }}
              >
                {sdToSolfege(sd)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* DictationInput — awaiting and graded phases */}
      {(isAwaiting || isGraded) && round && (
        <div className="mb-4">
          {isGraded && (
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded mb-1.5">
              Your answer
            </p>
          )}
          <DictationInput
            tonalityMode="major"
            tonicMidi={round.tonicMidi}
            range={round.range}
            chromaticEnabled={round.chromaticEnabled}
            sequence={sequence}
            onChange={onChangeSequence}
            perNote={grading?.perNote}
            disabled={isGraded}
            expectedLength={round.length}
          />
        </div>
      )}

      {/* Submit */}
      {!challengeMode && (
        <div className="mb-6">
          <button
            type="button"
            onClick={onSubmit}
            disabled={!isAwaiting || sequence.length === 0}
            className={buttonClasses("primary")}
            style={{ backgroundColor: modeColor }}
          >
            Submit →
          </button>
          {isAwaiting && (
            <span className="ml-3 font-mono text-[10px] text-inkFaded">Enter to submit · R to replay</span>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex gap-3 flex-wrap">
        {!challengeMode && (
          <button
            type="button"
            disabled={!isGraded && !isReveal}
            onClick={onNext}
            className={buttonClasses("primary")}
            style={{ backgroundColor: modeColor }}
          >
            {isLastRound ? "Finish →" : "Next →"}
          </button>
        )}
        {!challengeMode && (
          <button
            type="button"
            disabled={isPlaying}
            onClick={onReplay}
            className={buttonClasses("secondary")}
            aria-label="Replay the melody"
          >
            ♩ Replay
          </button>
        )}
        {!challengeMode && (
          <button
            type="button"
            disabled={!isGraded && !isReveal}
            onClick={onReplayTeach}
            className={buttonClasses("secondary")}
            aria-label="Replay at slow tempo"
          >
            ♩♩ Slow
          </button>
        )}
      </div>

      {(isGraded || isReveal) && !challengeMode && (
        <p className="mt-2 font-mono text-[10px] text-inkFaded">
          {isLastRound ? "Space/Enter to finish" : "Space/Enter for next"} · R to replay
        </p>
      )}
      {listenMode && <ListenAutoAdvanceControl isReveal={isReveal} onAdvance={onNext} />}
    </div>
  );
}

// ─── Session summary ──────────────────────────────────────────────────────────

interface DictationDrillSummaryProps {
  config: MelodicDictationExerciseConfig;
  sessionLength: number;
  accuracySum: number;
  roundsGraded: number;
  onAgain: () => void;
  onStop: () => void;
}

function DictationDrillSummary({
  config,
  sessionLength,
  accuracySum,
  roundsGraded,
  onAgain,
  onStop,
}: DictationDrillSummaryProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const isGraded = config.drillMode === "graded";
  const avgAccuracy =
    isGraded && roundsGraded > 0 ? Math.round(accuracySum / roundsGraded) : null;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        eyebrow="melodic dictation"
        title="Session complete"
      />

      <div className="grid grid-cols-2 border border-ink/10 mb-8">
        <div className="p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">Exercises</p>
          <p className="font-serif text-3xl font-medium text-ink">{sessionLength}</p>
        </div>
        {avgAccuracy !== null && (
          <div className="p-6 border-l border-ink/10">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">
              Avg accuracy
            </p>
            <p className="font-serif text-3xl font-medium text-ink">
              {avgAccuracy}
              <span className="text-lg text-inkSoft font-serif italic ml-0.5">%</span>
            </p>
          </div>
        )}
      </div>

      <div className="mb-6 font-mono text-xs text-inkFaded space-y-1">
        <p>{config.length} notes · {config.range} range · {config.chromatic ? "chromatic" : "diatonic"}</p>
        <p>{config.tonic === "random" ? "Random tonic" : "Fixed tonic"}</p>
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <button
          type="button"
          onClick={onAgain}
          className={buttonClasses("primary")}
        >
          Drill again →
        </button>
        <button
          type="button"
          onClick={onStop}
          className={buttonClasses("secondary")}
        >
          Change settings
        </button>
        <Link
          to="/exercise"
          className={buttonClasses("secondary")}
        >
          All drills
        </Link>
        <Link
          to="/stats"
          className={buttonClasses("secondary")}
        >
          See your stats →
        </Link>
        <ExerciseLessonLink nodeIds={["melodic-dictation-narrow", "melodic-dictation-medium", "melodic-dictation-chromatic"]} />
      </div>
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function ConfigRow({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-2">{label}</p>
      {children}
    </div>
  );
}

function NativeSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}): ReactNode {
  return (
    <div className="relative inline-block">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-3 pr-8 py-2 bg-paper border border-ink/20 font-mono text-sm text-ink focus:outline-none focus:border-ink appearance-none cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-inkFaded text-[10px]">
        ▾
      </span>
    </div>
  );
}
